import { useState } from "react";
import { TableHead } from "./table";
import { Popover, PopoverContent, PopoverTrigger } from "./popover";
import { Button } from "./button";
import { Input } from "./input";
import { Checkbox } from "./checkbox";
import { Filter, X, ChevronDown } from "lucide-react";
import { cn } from "../../lib/utils";

export function FilterableTableHeader({
    children,
    filterKey,
    options = [],
    selectedValues = [],
    onFilterChange,
    onOpenChange, // Add this prop
    placeholder = "Filter...",
    searchPlaceholder = "Search...",
    className = ""
}) {
    const [open, setOpen] = useState(false);
    const [searchTerm, setSearchTerm] = useState("");

    // Handle open change and notify parent
    const handleOpenChange = (newOpen) => {
        setOpen(newOpen);
        if (onOpenChange) {
            onOpenChange(filterKey, newOpen);
        }
    };

    const filteredOptions = options.filter((option) => {
        const label = typeof option === "string" ? option : (option.label || option.value || option);
        return label.toLowerCase().includes(searchTerm.toLowerCase());
    });

    const handleToggle = (value) => {
        const newSelected = selectedValues.includes(value)
            ? selectedValues.filter((v) => v !== value)
            : [...selectedValues, value];
        onFilterChange(filterKey, newSelected);
    };

    const handleSelectAll = () => {
        if (!filteredOptions.length) return;
        const allValues = Array.from(
            new Set(
                filteredOptions.map((option) =>
                    typeof option === "string"
                        ? option
                        : (option.value || option.label || option)
                )
            )
        );
        onFilterChange(filterKey, allValues);
    };

    const handleClear = () => {
        onFilterChange(filterKey, []);
        setOpen(false);
    };

    const hasActiveFilter = selectedValues.length > 0;

    return (
        <TableHead className={cn("relative", className)}>
            <Popover open={open} onOpenChange={handleOpenChange}>
                <PopoverTrigger asChild>
                    <Button
                        variant="ghost"
                        className={cn(
                            "h-auto p-0 font-semibold hover:bg-transparent flex items-center space-x-1 group",
                            hasActiveFilter && "text-blue-600"
                        )}
                    >
                        <span>{children}</span>
                        <div className="flex items-center space-x-1">
                            {hasActiveFilter && (
                                <span className="bg-indigo-600 text-white text-xs rounded-full h-5 w-5 flex items-center justify-center">
                                    {selectedValues.length}
                                </span>
                            )}
                            <Filter className={cn(
                                "h-3 w-3 opacity-0 group-hover:opacity-100 transition-opacity",
                                hasActiveFilter && "opacity-100 text-blue-600"
                            )} />
                        </div>
                    </Button>
                </PopoverTrigger>
                <PopoverContent className="w-64 p-0" align="start">
                    <div className="p-3 border-b">
                        <div className="flex items-center justify-between mb-2">
                            <span className="text-sm font-semibold">Filter by {children}</span>
                            <div className="flex items-center gap-1">
                                {filteredOptions.length > 0 && (
                                    <Button
                                        variant="ghost"
                                        size="sm"
                                        onClick={handleSelectAll}
                                        className="h-6 px-2 text-xs"
                                    >
                                        Select all
                                    </Button>
                                )}
                                {hasActiveFilter && (
                                    <Button
                                        variant="ghost"
                                        size="sm"
                                        onClick={handleClear}
                                        className="h-6 px-2 text-xs"
                                    >
                                        <X className="h-3 w-3 mr-1" />
                                        Clear
                                    </Button>
                                )}
                            </div>
                        </div>
                        <Input
                            placeholder={searchPlaceholder}
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            className="h-8"
                        />
                    </div>
                    <div className="max-h-60 overflow-y-auto p-2">
                        {filteredOptions.length === 0 ? (
                            <div className="text-sm text-gray-500 text-center py-4">
                                No options found
                            </div>
                        ) : (
                            <div className="space-y-1">
                                {filteredOptions.map((option) => {
                                    const value = typeof option === "string" ? option : (option.value || option.label || option);
                                    const label = typeof option === "string" ? option : (option.label || option.value || option);
                                    const isSelected = selectedValues.includes(value);

                                    return (
                                        <div
                                            key={value}
                                            className="flex items-center space-x-2 p-2 hover:bg-gray-100 rounded cursor-pointer"
                                            onClick={() => handleToggle(value)}
                                        >
                                            <Checkbox
                                                checked={isSelected}
                                                onCheckedChange={() => handleToggle(value)}
                                            />
                                            <span className="text-sm flex-1">{label}</span>
                                        </div>
                                    );
                                })}
                            </div>
                        )}
                    </div>
                    {hasActiveFilter && (
                        <div className="p-2 border-t bg-gray-50">
                            <div className="text-xs text-gray-600">
                                {selectedValues.length} selected
                            </div>
                        </div>
                    )}
                </PopoverContent>
            </Popover>
        </TableHead>
    );
}

